import React, { useEffect, useState } from "react";
import {
  Button,
  TextInput,
  Table,
  Modal,
  Label,
  Spinner,
  Tooltip,
  Badge,
  Select,
} from "flowbite-react";
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiQuestionMarkCircle,
} from "react-icons/hi";
import { SYSTEM_VARIABLES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";
import { useApplication } from "../contexts/ApplicationContext";

const KNOWN_KEYS = ["USER_BVS", "PASSWORD_BVS", "USER_SPC", "PASSWORD_SPC"];
const CUSTOM_VALUE = "__CUSTOM__";

const isSensitiveKey = (key = "") =>
  String(key).toUpperCase().includes("PASSWORD");

export default function SystemVariablesManagerPage() {
  const { user } = useApplication();
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);

  // form (valor e descrição continuam aqui)
  const [form, setForm] = useState({
    value: "",
    description: "",
  });

  // controle da "chave": via select + custom quando Outros
  const [selectKey, setSelectKey] = useState("");
  const [customKey, setCustomKey] = useState("");

  const [editingKey, setEditingKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  const loadVariables = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(SYSTEM_VARIABLES.GET_ALL);
      setVariables(data.data || []);
    } catch (err) {
      console.error("Erro ao carregar variáveis:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVariables();
  }, []);

  const resetForm = () => {
    setForm({ value: "", description: "" });
    setSelectKey("");
    setCustomKey("");
  };

  const openModal = () => {
    resetForm();
    setEditingKey(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    resetForm();
    setEditingKey(null);
    setModalOpen(false);
  };

  const askConfirmation = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmModalOpen(true);
  };

  const confirm = () => {
    if (confirmAction) confirmAction();
    setConfirmModalOpen(false);
  };

  const handleSave = () => {
    const keyToSave =
      selectKey === CUSTOM_VALUE ? customKey.trim() : (selectKey || "").trim();

    if (!keyToSave) return; // precisa ter chave
    if (selectKey === CUSTOM_VALUE && customKey.trim() === "") return; // custom obrigatório
    if (form.value === "") return;

    const isEdit = !!editingKey;

    askConfirmation(
      isEdit
        ? `Deseja atualizar a variável "${keyToSave}"?`
        : `Deseja criar a variável "${keyToSave}"?`,
      async () => {
        try {
          const payload = {
            key: keyToSave,
            value: form.value,
            description: form.description?.trim() || "",
            companyId: user?.companyId,
          };

          await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, payload);
          closeModal();
          loadVariables();
        } catch (err) {
          console.error("Erro ao salvar variável:", err);
        }
      }
    );
  };

  const handleEdit = ({ key, value, description }) => {
    // se a chave está na lista conhecida, seleciona-a; senão, vai para Outros
    if (KNOWN_KEYS.includes(key)) {
      setSelectKey(key);
      setCustomKey("");
    } else {
      setSelectKey(CUSTOM_VALUE);
      setCustomKey(key);
    }

    setForm({
      value,
      description: description || "",
    });
    setEditingKey(key);
    setModalOpen(true);
  };

  const handleDelete = ({ key }) => {
    askConfirmation(`Deseja excluir a variável "${key}"?`, async () => {
      try {
        const qs =
          user?.companyId == null ? "" : `?companyId=${user?.companyId}`;
        await axiosInstance.delete(
          `${SYSTEM_VARIABLES.DELETE}/${encodeURIComponent(key)}${qs}`
        );
        loadVariables();
      } catch (err) {
        console.error("Erro ao excluir variável:", err);
      }
    });
  };

  const KEY_LABELS = {
    USER_BVS_RELINQ: "USUARIO_BOA_VISTA",
    PASSWORD_BVS_RELINQ: "SENHA_BOA_VISTA",
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="mb-3 text-xl font-semibold text-gray-800">
        Nenhuma variável configurada ainda
      </div>
      <p className="text-gray-600 max-w-xl">
        Esta é uma área <b>configurável</b> para você configurar o seu próprio
        contrato (chaves, tokens e parâmetros por empresa). Comece criando a sua
        primeira variável.
      </p>
      <Button className="mt-6" onClick={openModal}>
        <div className="flex items-center gap-2">
          <HiPlus className="w-4 h-4" />
          <span>Criar primeira variável</span>
        </div>
      </Button>
    </div>
  );

  return (
    <div className="p-4 mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Variáveis do Sistema
        </h1>
        <div className="flex items-center gap-3">
          <Button
            color="gray"
            size="xs"
            onClick={() => setHelpOpen(true)}
            aria-label="Abrir explicação"
          >
            <HiQuestionMarkCircle className="w-5 h-5" />
          </Button>

          <Button onClick={openModal}>
            <div className="flex items-center gap-2">
              <HiPlus className="w-4 h-4" />
              <span>Nova Variável</span>
            </div>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : variables.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <Table striped hoverable>
            <Table.Head>
              <Table.HeadCell>Descrição</Table.HeadCell>
              <Table.HeadCell>Chave</Table.HeadCell>
              <Table.HeadCell>Valor</Table.HeadCell>
              <Table.HeadCell>Atualizado em</Table.HeadCell>
              <Table.HeadCell className="text-center">Ações</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {variables.map((variable) => (
                <Table.Row
                  key={`${variable.key}-${variable.companyId ?? "global"}`}
                >
                  <Table.Cell>{variable.description || "—"}</Table.Cell>
                  <Table.Cell className="font-medium text-gray-900">
                    {KEY_LABELS[variable.key] ?? variable.key}
                  </Table.Cell>

                  <Table.Cell className="max-w-sm truncate">
                    {isSensitiveKey(variable.key) ? "*******" : variable.value}
                  </Table.Cell>

                  <Table.Cell>
                    {variable.updatedAt
                      ? new Date(variable.updatedAt).toLocaleString("pt-BR")
                      : "—"}
                  </Table.Cell>
                  <Table.Cell className="flex justify-center space-x-2">
                    <Tooltip content="Editar">
                      <Button
                        size="xs"
                        onClick={() => handleEdit(variable)}
                        title="Editar"
                      >
                        <HiPencil className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Excluir">
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => handleDelete(variable)}
                        title="Excluir"
                      >
                        <HiTrash className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Modal de criação/edição */}
      <Modal show={modalOpen} onClose={closeModal}>
        <Modal.Header>
          {editingKey ? "Editar Variável" : "Nova Variável"}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {/* CHAVE: Select + campo Outros */}
            <div>
              <Label htmlFor="key" value="Chave" />
              <Select
                id="key"
                value={selectKey}
                onChange={(e) => setSelectKey(e.target.value)}
                disabled={!!editingKey}
                required
              >
                <option value="" disabled>
                  Selecione...
                </option>
                <option value="USER_BVS_RELINQ">Usuário Boa vista</option>
                <option value="PASSWORD_BVS_RELINQ">Senha Boa vista</option>
                <option value="USER_SPC">Operador SPC</option>
                <option value="PASSWORD_SPC">Senha SPC</option>
                <option value={CUSTOM_VALUE}>Outros</option>
              </Select>
            </div>

            {selectKey === CUSTOM_VALUE && (
              <div>
                <Label htmlFor="customKey" value="Chave personalizada" />
                <TextInput
                  id="customKey"
                  placeholder="Ex.: TOKEN_ESPECIFICO"
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value)}
                  disabled={!!editingKey}
                  required
                />
              </div>
            )}

            <div>
              <Label htmlFor="value" value="Valor" />
              <TextInput
                id="value"
                type={
                  isSensitiveKey(
                    selectKey === CUSTOM_VALUE ? customKey : selectKey
                  )
                    ? "password"
                    : "text"
                }
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="description" value="Descrição" />
              <TextInput
                id="description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSave}>Salvar</Button>
          <Button color="gray" onClick={closeModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação */}
      <Modal show={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
        <Modal.Header>Confirmação</Modal.Header>
        <Modal.Body>
          <p className="text-gray-700">{confirmMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={confirm}>
            Confirmar
          </Button>
          <Button color="gray" onClick={() => setConfirmModalOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal de ajuda */}
      <Modal show={helpOpen} onClose={() => setHelpOpen(false)}>
        <Modal.Header>Sobre “Variáveis do Sistema”</Modal.Header>
        <Modal.Body>
          <div className="space-y-3 text-gray-700">
            <p>
              Esta seção é <b>configurável</b> para você gerenciar variáveis do
              seu próprio contrato, como usuários, senhas e tokens de
              integrações (ex.: Boa Vista, SPC, etc.).
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <b>Chave</b>: selecione uma opção conhecida ou use <i>Outros</i>{" "}
                para criar sua própria chave.
              </li>
              <li>
                <b>Valor</b>: informe o conteúdo (token, login, senha ou
                parâmetro).
              </li>
              <li>
                <b>Descrição</b>: ajude seu time a entender onde a variável é
                usada.
              </li>
            </ul>
            <p className="text-sm text-gray-500">
              Dica: variáveis são salvas por empresa. O backend diferencia pelo
              par
              <code> (companyId, key) </code>.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setHelpOpen(false)}>Entendi</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
